import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Modal,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Typography,
    TextField,
    InputAdornment,
    CircularProgress,
    Menu,
    MenuItem,
    IconButton,
    Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getPresentAbsent } from "../../../services/reportServices/adminReportServices/AttendanceReportServices";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#7e7e7e",
        color: theme.palette.common.white,
        textTransform: "capitalize",
        fontSize: 16,
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd),&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

function highlightText(text, search) {
    if (!search || !text) return text;
    const parts = text.toString().split(new RegExp(`(${search})`, "gi"));
    return parts.map((part, index) =>
        part.toLowerCase() === search.toLowerCase() ? (
            <span key={index} style={{ backgroundColor: "yellow" }}>
                {part}
            </span>
        ) : (
            part
        )
    );
}

function AdminAttendanceDetailsModal({ open, onClose, selected }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [search, setSearch] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        if (!open) return;
        const fetchAdminAttendance = async () => {
            try {
                setLoading(true);
                if (selected?.coe && selected?.lab && selected?.date) {
                    const response = await getPresentAbsent(
                        selected.coe,
                        selected.lab,
                        selected.date,
                        selected.attendanceId
                    );
                    setRows(response?.data?.data || []);
                }
            } catch (error) {
                toast.error(
                    error?.response?.data?.message ||
                    error?.message ||
                    "Error fetching attendance data"
                );
            } finally {
                setLoading(false);
            }
        };

        fetchAdminAttendance();
    }, [selected]);

    const allLearners =
        selected.action === "Present"
            ? rows[0]?.presentLearners || []
            : rows[0]?.absentLearners || [];

    const filteredData = allLearners.filter((item) => {
        const value = search.toLowerCase();
        return (
            
            item?.StudentRollNo?.toString().toLowerCase().includes(value) ||
            item?.StudentName?.toLowerCase().includes(value) ||
            item?.StudentEmail?.toLowerCase().includes(value) ||
            item?.StudentPhone?.toString().toLowerCase().includes(value) ||
            item?.Status?.toLowerCase().includes(value) ||
            selected?.date?.toLowerCase().includes(value)
        );
    });

    const paginatedData = filteredData?.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDownloadClick = (event) => setAnchorEl(event.currentTarget);
    const handleCloseMenu = () => setAnchorEl(null);

    const handleDownload = (type) => {
        handleCloseMenu();

        if (!filteredData.length) {
            toast.warning("No data to export!");
            return;
        }

        const formatted = filteredData.map((r) => ({
            Date: selected.date || "-",
            "Roll No": r.StudentRollNo || "-",
            "Student Name": r.StudentName || "-",
            "Student Email": r.StudentEmail || "-",
            "Student Phone": r.StudentPhone || "-",
            Status: r.Status || "-",
        }));

        if (type === "csv") {
            const csv = Papa.unparse(formatted);
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${modalTitle.replace(/\s+/g, "_")}_Report.csv`;
            link.click();
        }

        if (type === "pdf") {
            const doc = new jsPDF();
            autoTable(doc, {
                startY: 20,
                head: [[
                    "Date",
                    "Roll No",
                    "Name",
                    "Email",
                    "Phone",
                    "Status",
                ]],
                body: formatted.map((r) => Object.values(r)),
                theme: "grid",
                headStyles: {
                    fillColor: [126, 126, 126],
                    halign: "center",
                    valign: "middle"
                },
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    overflow: "linebreak",
                    halign: "center",
                    valign: "middle",
                    lineColor: [200, 200, 200],
                    lineWidth: 0.1,
                },
                columnStyles: {
                    0: { cellWidth: 22 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 22 },
                    3: { cellWidth: 35 },
                    4: { cellWidth: 35 },
                    5: { cellWidth: 30 },
                    6: { cellWidth: 20 },
                },
                didDrawPage: () => {
                    doc.setFontSize(12);
                    doc.text(`${modalTitle} Report`, 14, 15);
                },
            });

            doc.save(`${modalTitle.replace(/\s+/g, "_")}_Report.pdf`);
        }
    };

    const modalTitle =
        selected?.action === "Present"
            ? "Attended Students"
            : selected?.action === "Absent"
            ? "Not Attended Students"
            : "Attendance Details";

    const labName = selected?.lab_name || selected?.labName || (rows && rows[0]?.lab_name) || (allLearners && allLearners[0]?.lab_name) || selected?.lab || "-";
    const coeName = selected?.coe_name || selected?.coeName || (rows && rows[0]?.coe_name) || (allLearners && allLearners[0]?.coe_name) || "";
    const dateVal = selected?.date || (rows && rows[0]?.date) || (allLearners && allLearners[0]?.AttendanceDate) || "-";
    const attendanceName = selected?.attendanceName || (allLearners && allLearners[0]?.AttendanceName) || "";

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    outline: "none !important",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "95%", sm: "90%", md: "85%" },
                    maxWidth: "95vw",
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    maxHeight: "90vh !important",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" pb={2}>
                    <Box>
                        <Typography
                            className="dashboard-header-title"
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            {modalTitle}
                        </Typography>

                        {!loading && (
                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                                Lab: <strong>{labName}</strong>
                                {coeName ? <>&nbsp;|&nbsp; COE: <strong>{coeName}</strong></> : null}
                                {dateVal && dateVal !== "-" ? <>&nbsp;|&nbsp; Date: <strong>{dateVal}</strong></> : null}
                                {attendanceName && attendanceName !== "-" ? <>&nbsp;|&nbsp; Attendance: <strong>{attendanceName}</strong></> : null}
                                &nbsp;|&nbsp; Total: <strong>{allLearners.length}</strong>
                            </Typography>
                        )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                        <TextField
                            size="small"
                            placeholder="Search by name, roll no, email..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            sx={{ minWidth: "280px" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Box>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadClick}
                            >
                                Download
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleCloseMenu}
                            >
                                <MenuItem onClick={() => handleDownload("csv")}>
                                    Download CSV
                                </MenuItem>
                                <MenuItem onClick={() => handleDownload("pdf")}>
                                    Download PDF
                                </MenuItem>
                            </Menu>
                        </Box>
                        <Tooltip title="Close">
                            <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            component={Paper}
                            sx={{
                                maxHeight: "calc(90vh - 150px)",
                                overflow: "auto",
                            }}
                        >
                            <Table stickyHeader sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Date</StyledTableCell>
                                        <StyledTableCell>Roll No</StyledTableCell>
                                        <StyledTableCell>Student Name</StyledTableCell>
                                        <StyledTableCell>Student Email</StyledTableCell>
                                        <StyledTableCell>Student Phone</StyledTableCell>
                                        <StyledTableCell>Status</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedData.length ? (
                                        paginatedData.map((row, index) => (
                                            <StyledTableRow key={index}>
                                                <StyledTableCell>
                                                    {highlightText(selected.date || "-", search)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {highlightText(row.StudentRollNo?.toString() || "-", search)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {highlightText(row.StudentName || "-", search)}
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ textTransform: "none" }}>
                                                    {highlightText(row.StudentEmail || "-", search)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {highlightText(row.StudentPhone || "-", search)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {highlightText(row.Status || "-", search)}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <StyledTableRow>
                                            <StyledTableCell colSpan={6} align="center">
                                                No records found
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[10, 20, 30, 60, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                )}
            </Box>
        </Modal>
    );
}

export default AdminAttendanceDetailsModal;
